import { CSSProperties } from "react";
import { AggregationType } from "./fields";
import { Filter } from "./records";
import { Right } from "./Users";
import { ASC, SortType, SplitBySortType } from "./views";

export type Dashboard = {
    _key: string;
    ownerKey: string | null;
    name: string;
    lines: DashboardLine[];
    order: number;
    shared: boolean;
}

export type DashboardLine = {
    widgets: Widget[];
}

export type WidgetType = "KPI" | "PIE_CHART" | "BAR_CHART" | "LINE_CHART" | "RADAR_CHART" | "TEXT"
export const WidgetTypes: WidgetType[] = ["KPI", "PIE_CHART", "BAR_CHART", "LINE_CHART", "RADAR_CHART", "TEXT"];
export const KPIWidgetType: WidgetType = "KPI";
export const PieChartWidgetType: WidgetType = "PIE_CHART";
export const BarChartWidgetType: WidgetType = "BAR_CHART";
export const LineChartWidgetType: WidgetType = "LINE_CHART";
export const RadarChartWidgetType: WidgetType = "RADAR_CHART";
export const TextWidgetType: WidgetType = "TEXT";

export type Widget = {
    type: WidgetType;
    title: string | null;
    description: string | null;
    data: WidgetData;
    width: number | null;
    height: number | null;
    showLegend: boolean;
    showValues: boolean;
    showPercentage: boolean;
    hideYAxis: boolean;
}

export class WidgetData {
    filter: Filter | null = null;

    constructor(filter: Filter | null = null) {
        this.filter = filter;
    }
}

export class KPIData extends WidgetData {
    valuePath: string | null = null;
    valueAggregation: AggregationType | null = null;

    constructor(
        valuePathKey: string | null = null,
        valueAggregation: AggregationType | null = null,
        filter: Filter | null = null) {

        super(filter);
        this.valuePath = valuePathKey;
        this.valueAggregation = valueAggregation;
    }
}

export class PieChartData extends WidgetData {
    valuePath: string | null = null;
    valueAggregation: AggregationType | null = null;
    sortByValue: boolean = false;
    categoryPath: string | null = null;
    categoryAggregation: AggregationType | null = null;
    categorySortType: SortType = ASC;

    constructor(
        valuePathKey: string | null = null,
        valueAggregation: AggregationType | null = null,
        categoryPathKey: string | null = null,
        categoryAggregation: AggregationType | null = null,
        categorySortType: SortType = ASC,
        filter: Filter | null = null) {

        super(filter);
        this.valuePath = valuePathKey;
        this.valueAggregation = valueAggregation;
        this.categoryPath = categoryPathKey;
        this.categoryAggregation = categoryAggregation;
        this.categorySortType = categorySortType;
    }
}

export class BarChartData extends WidgetData {
    valuePath: string | null = null;
    valueAggregation: AggregationType | null = null;
    splitByPath: string | null = null;
    splitByAggregation: AggregationType | null = null;
    splitBySortType: SplitBySortType = ASC;
    categoryByYear: boolean = false;
    categoryPath: string | null = null;
    categoryAggregation: AggregationType | null = null;
    categorySortType: SortType = ASC;
    showEmptyCategories: boolean = false;

    constructor(
        valuePathKey: string | null = null,
        valueAggregation: AggregationType | null = null,
        splitByPathKey: string | null = null,
        splitByAggregation: AggregationType | null = null,
        splitBySortType: SplitBySortType = ASC,
        categoryByYear: boolean = false,
        categoryPathKey: string | null = null,
        categoryAggregation: AggregationType | null = null,
        categorySortType: SortType = ASC,
        showEmptyCategories: boolean = false,
        filter: Filter | null = null) {

        super(filter);
        this.valuePath = valuePathKey;
        this.valueAggregation = valueAggregation;
        this.splitByPath = splitByPathKey;
        this.splitByAggregation = splitByAggregation;
        this.splitBySortType = splitBySortType;
        this.categoryPath = categoryPathKey;
        this.categoryAggregation = categoryAggregation;
        this.categorySortType = categorySortType;
        this.showEmptyCategories = showEmptyCategories;
        this.categoryByYear = categoryByYear;
    }
}

export class RadarChartData extends WidgetData {
    valuePath: string | null = null;
    valueAggregation: AggregationType | null = null;
    splitByPath: string | null = null;
    splitByAggregation: AggregationType | null = null;
    splitBySortType: SortType = ASC;
    categoryByYear: boolean = false;
    categoryPath: string | null = null;
    categoryAggregation: AggregationType | null = null;
    categorySortType: SortType = ASC;
    showEmptyCategories: boolean = false;

    constructor(
        valuePathKey: string | null = null,
        valueAggregation: AggregationType | null = null,
        splitByPathKey: string | null = null,
        splitByAggregation: AggregationType | null = null,
        splitBySortType: SortType = ASC,
        categoryByYear: boolean = false,
        categoryPathKey: string | null = null,
        categoryAggregation: AggregationType | null = null,
        categorySortType: SortType = ASC,
        showEmptyCategories: boolean = false,
        filter: Filter | null = null) {

        super(filter);
        this.valuePath = valuePathKey;
        this.valueAggregation = valueAggregation;
        this.splitByPath = splitByPathKey;
        this.splitByAggregation = splitByAggregation;
        this.splitBySortType = splitBySortType;
        this.categoryByYear = categoryByYear;
        this.categoryPath = categoryPathKey;
        this.categoryAggregation = categoryAggregation;
        this.categorySortType = categorySortType;
        this.showEmptyCategories = showEmptyCategories;
    }
}

export enum LineStyle {
    BASIS = 'basis',
    BUMP = 'bump',
    LINEAR = 'linear',
    NATURAL = 'natural',
    MONOTONE = 'monotone',
}

export class LineChartData extends WidgetData {
    valuePath: string | null = null;
    valueAggregation: AggregationType | null = null;
    splitByPath: string | null = null;
    splitByAggregation: AggregationType | null = null;
    splitBySortType: SortType = ASC;
    categoryPath: string | null = null;
    categoryAggregation: AggregationType | null = null;
    categorySortType: SortType = ASC;
    lineStyle: LineStyle = LineStyle.MONOTONE;
    showDots: boolean = false;
    nullsAsZeros: boolean = false;
    categoryByYear: boolean = false;
    showEmptyCategories: boolean = false;
    constructor(
        valuePathKey: string | null = null,
        valueAggregation: AggregationType | null = null,
        splitByPathKey: string | null = null,
        splitByAggregation: AggregationType | null = null,
        splitBySortType: SortType = ASC,
        categoryByYear: boolean = false,
        categoryPathKey: string | null = null,
        categoryAggregation: AggregationType | null = null,
        categorySortType: SortType = ASC,
        lineStyle: LineStyle = LineStyle.MONOTONE,
        showDots: boolean = false,
        nullsAsZeros: boolean = false,
        showEmptyCategories: boolean = false,
        filter: Filter | null = null) {

        super(filter);
        this.valuePath = valuePathKey;
        this.valueAggregation = valueAggregation;
        this.splitByPath = splitByPathKey;
        this.splitByAggregation = splitByAggregation;
        this.splitBySortType = splitBySortType;
        this.categoryPath = categoryPathKey;
        this.categoryAggregation = categoryAggregation;
        this.categorySortType = categorySortType;
        this.lineStyle = lineStyle;
        this.showDots = showDots;
        this.nullsAsZeros = nullsAsZeros;
        this.categoryByYear = categoryByYear;
        this.showEmptyCategories = showEmptyCategories;
    }
}

export class TextData extends WidgetData {
    markdown: string = "";

    constructor(markdown: string = "") {
        super(null);
        this.markdown = markdown;
    }
}

export type ChartDataItem = {
    splitBy: string | null;
    category: string | null;
    value: number;
    fill: string;
}

type WidgetDimension = {
    width: string;
    height: string;
}

const WidgetDimensions: Map<WidgetType, WidgetDimension> = new Map([
    [KPIWidgetType, { width: "200px", height: "auto" }],
    [PieChartWidgetType, { width: "200px", height: "auto" }],
    [BarChartWidgetType, { width: "200px", height: "auto" }],
    [LineChartWidgetType, { width: "200px", height: "auto" }],
    [RadarChartWidgetType, { width: "200px", height: "auto" }],
    [TextWidgetType, { width: "400px", height: "auto" }],
]);

export function getWidgetStyle(widget: Widget): CSSProperties {
    const widgetDimension = WidgetDimensions.get(widget.type);
    let widgetWidth = widgetDimension?.width ?? "auto";
    let widgetHeight = widgetDimension?.height ?? "auto";
    if (widget.width) {
        if (typeof widget.width === "number") {
            widgetWidth = `${widget.width}px`;
        }
    }
    if (widget.height) {
        if (typeof widget.height === "number") {
            widgetHeight = `${widget.height}px`;
        }
    }
    const style = {
        width: widgetWidth,
        height: widgetHeight,
    }
    return style;
}

export interface WidgetProps {
    widget: Widget;
}

export interface ShareDashboardRequest {
    dashboardKey: string;
    roleKey?: string;
    userKey?: string;
    right: Right | null;
}

